import { format as d3Format } from 'd3-format';
import type { GeoJsonProperties } from 'geojson';
import { isNil } from 'lodash';
import { useQuery } from 'urql';

import { FeatureFlag } from '@revelio/core';
import { useUnleashFlag } from '@revelio/core/use-unleash-flag';
import {
  COMPOSITION_TOOLTIP,
  COMPOSITION_V3_TOOLTIP,
  type CompositionTooltipQuery,
  type CompositionTooltipQueryVariables,
  type CompositionV2Entity,
  type CompositionV2Filters,
  type CompositionV2FullSnapshotReq,
  type CompositionV3TooltipQuery,
  type CompositionV3TooltipQueryVariables,
  Dimension1,
  PAY_V2_TOOLTIP_QUERY,
  type PayV2Entity,
  type PayV2EntResp,
  POSTING_TOOLTIP,
  PositionsEntityKind,
  type PostingFilters,
} from '@revelio/data-access';
import { formatterWithExchangeRate } from '@revelio/replots';

import { calculateMarketTightnessMeterValue } from '../../next/CustomDash/components/DataProvider/market-conditions/z-score/calculate-meter-value';
import type { DistributionData } from '../../next/CustomDash/components/DataProvider/types';
import { getDistributionDataFromPayV2 } from '../../next/CustomDash/components/DataProvider/utils/convert-compensation-data';
import { DEFAULT_SKILL_TAXONOMY_ID } from '../../next/CustomDash/filters/contexts/defaultFilters';
import { getCompositionV3BreakdownOptions } from '../../next/CustomDash/filters/utils/composition-v3/getCompositionV3BreakdownOptions';
import {
  mapTooltipFiltersToCompositionV3Corrections,
  mapTooltipFiltersToCompositionV3Filters,
} from '../../next/CustomDash/filters/utils/composition-v3/map-tooltip-filters-to-v3';
import {
  mapCompositionV2EntitiesToV3,
  stripEntitiesFromFilters,
} from '../../next/CustomDash/filters/utils/composition-v3/role-skill-ent-kind';
import { useMonthApiFilters } from '../../next/entity-summary/use-month-api-filters';
import { useCurrencyPreference } from '../user-preferences/hooks/useCurrencyPreference';

export interface TooltipData {
  header: string;
  headcount?: string;
  postings?: string;
  timeToFill?: string;
  salaryDist?: string[];
  marketTightness?: string;
  isLoading: boolean;
  height?: number;
  showSalaryData?: boolean;
}

export interface UseFetchTooltipDataParams {
  dim1: Dimension1;
  tooltipFeature: GeoJsonProperties | null;
  compositionEntities?: CompositionV2Entity[];
  compositionFilters?: CompositionV2Filters;
  postingFilters?: PostingFilters;
  skipComposition?: boolean;
  skipPosting: boolean; // Users who don't have Posting tab access should skip this query which will result in - postings
  skipCompensation?: boolean;
}

const formatInt = d3Format(',.0f');

type TooltipCompositionMode = 'legacy' | 'v3';

function useFetchTooltipDataInner(
  compositionMode: TooltipCompositionMode,
  {
    dim1,
    tooltipFeature,
    compositionEntities = [],
    compositionFilters,
    postingFilters = {},
    skipComposition = false,
    skipPosting = false,
    skipCompensation = false,
  }: UseFetchTooltipDataParams
): TooltipData | null {
  const isV3Tooltip = compositionMode === 'v3';
  const { code: currencyCode, exchangeRate } = useCurrencyPreference();
  const currencyFormatter = formatterWithExchangeRate(
    currencyCode,
    exchangeRate
  );
  const hasGranularSelection =
    hasGranularGeoSelection(compositionFilters) ||
    hasGranularGeoSelection(postingFilters);
  const geoFilter = getTooltipGeoFilter(tooltipFeature, hasGranularSelection);
  const mapTooltipEntities = buildTooltipEntities(tooltipFeature, []);
  const useTooltipEntity =
    mapTooltipEntities.length > 0 ||
    (dim1 === Dimension1.Geography && !isNil(geoFilter));

  const compositionEntitiesEffective = useTooltipEntity
    ? buildTooltipEntities(tooltipFeature, compositionEntities)
    : compositionEntities;

  const compositionWithTooltipGeoFilters = replaceGeographyFilters(
    compositionFilters,
    useTooltipEntity ? {} : geoFilter
  );

  const postingGeoFilter = useTooltipEntity
    ? getTooltipGeoFilter(tooltipFeature, false)
    : geoFilter;

  const postingWithTooltipGeoFilters = replaceGeographyFilters(
    postingFilters,
    postingGeoFilter
  );

  const dateRangeApiFilters = useMonthApiFilters();

  const {
    raw: _raw,
    start_date: _start_date,
    end_date: _end_date,
    ...compositionFiltersForQuery
  } = compositionWithTooltipGeoFilters ?? {};

  const snapshotDateForComposition = dateRangeApiFilters?.date_range_api_filters
    ?.end_date as string;

  const compositionTooltipVariablesLegacy = {
    entities: compositionEntitiesEffective,
    filters: {
      ...compositionFiltersForQuery,
      snapshot_date: snapshotDateForComposition,
    },
  };

  const customRoleForTaxonomy = compositionFiltersForQuery?.custom_role;
  const customSkillForTaxonomy = compositionFiltersForQuery?.custom_skill;
  const role_taxonomy =
    customRoleForTaxonomy?.taxonomyId?.taxonomyId !== undefined &&
    customRoleForTaxonomy.taxonomyId.taxonomyId !== null
      ? customRoleForTaxonomy.taxonomyId.taxonomyId
      : 0;
  const skill_taxonomy =
    customSkillForTaxonomy?.taxonomy_id !== undefined &&
    customSkillForTaxonomy.taxonomy_id !== null
      ? customSkillForTaxonomy.taxonomy_id
      : DEFAULT_SKILL_TAXONOMY_ID;

  const compositionV3FiltersForTooltip =
    mapTooltipFiltersToCompositionV3Filters({
      filters: compositionFiltersForQuery as Partial<CompositionV2Filters>,
      snapshotDate: snapshotDateForComposition,
    });

  const compositionV3EntitiesForTooltip =
    mapCompositionV2EntitiesToV3(compositionEntitiesEffective) ?? [];

  const compositionV3FiltersStripped = stripEntitiesFromFilters(
    compositionV3FiltersForTooltip,
    compositionV3EntitiesForTooltip
  );

  const compositionV3CorrectionsForTooltip =
    mapTooltipFiltersToCompositionV3Corrections({
      filters: compositionFiltersForQuery as Partial<CompositionV2Filters>,
    });

  const tooltipBreakdownOptions = getCompositionV3BreakdownOptions({
    compositionFilters: compositionV3FiltersStripped,
    geographyMode: 'msa',
  });

  const compositionTooltipVariablesV3 = {
    entities: compositionV3EntitiesForTooltip,
    filters: compositionV3FiltersStripped,
    corrections: compositionV3CorrectionsForTooltip,
    role_taxonomy,
    skill_taxonomy,
    breakdown_options: {
      geo_breakdown_level: tooltipBreakdownOptions.geo_breakdown_level,
      rics_breakdown_level: tooltipBreakdownOptions.rics_breakdown_level,
      role_breakdown_level: tooltipBreakdownOptions.role_breakdown_level,
      skill_breakdown_level: tooltipBreakdownOptions.skill_breakdown_level,
    },
  };

  const compositionTooltipQuery = isV3Tooltip
    ? COMPOSITION_V3_TOOLTIP
    : COMPOSITION_TOOLTIP;
  const compositionTooltipVariables = isV3Tooltip
    ? compositionTooltipVariablesV3
    : compositionTooltipVariablesLegacy;

  const [{ data: compositionTooltipData, fetching: compositionFetching }] =
    useQuery<
      CompositionTooltipQuery | CompositionV3TooltipQuery,
      CompositionTooltipQueryVariables | CompositionV3TooltipQueryVariables
    >({
      query: compositionTooltipQuery,
      variables: compositionTooltipVariables as
        | CompositionTooltipQueryVariables
        | CompositionV3TooltipQueryVariables,
      pause:
        skipComposition ||
        !tooltipFeature ||
        (isV3Tooltip && compositionV3EntitiesForTooltip.length === 0),
    });

  const [{ data: postingData, fetching: postingFetching }] = useQuery({
    query: POSTING_TOOLTIP,
    variables: {
      dim1: dim1,
      filters: {
        ...postingWithTooltipGeoFilters,
        start_date:
          dateRangeApiFilters?.date_range_full_api_filters?.start_date,
        end_date: dateRangeApiFilters?.date_range_full_api_filters?.end_date,
        metric_mode: 'expected_hires',
      },
    },
    pause: skipPosting || !tooltipFeature,
  });

  const [{ data: compensationData, fetching: compensationFetching }] = useQuery(
    {
      query: PAY_V2_TOOLTIP_QUERY,
      variables: {
        req: {
          entities: compositionEntitiesEffective as PayV2Entity[],
          filters: compositionWithTooltipGeoFilters,
        },
      },
      pause: skipCompensation || !tooltipFeature,
    }
  );

  const distributionData = getDistributionDataFromPayV2(
    compensationData?.pay_v2?.entities as PayV2EntResp[],
    'total_compensation'
  );

  if (!tooltipFeature) {
    return null;
  }

  const { headcount: headcountFmt, headcountNumber } = (() => {
    if (!compositionTooltipData) return { headcount: '-' };

    if (isV3Tooltip && 'composition_v3' in compositionTooltipData) {
      const entity = compositionTooltipData.composition_v3?.entities?.[0];
      if (!entity) return { headcount: '-' };
      const headcounts = entity.metrics?.headcount;
      const lastHeadcount = headcounts?.[headcounts.length - 1];
      const headcount = lastHeadcount?.val;
      if (headcount === undefined) return { headcount: '-' };
      return { headcount: formatInt(headcount), headcountNumber: headcount };
    }

    if (!('composition_v2_tooltip' in compositionTooltipData)) {
      return { headcount: '-' };
    }

    const entity = compositionTooltipData.composition_v2_tooltip?.entities?.[0];
    if (!entity) return { headcount: '-' };

    const headcounts = entity?.scaled?.headcount;
    const lastHeadcount = headcounts?.[headcounts.length - 1];
    const headcount = lastHeadcount?.value;
    if (!headcount) return { headcount: '-' };

    return { headcount: formatInt(headcount), headcountNumber: headcount };
  })();

  const {
    postings: postingFmt,
    postingsNumber,
    timeToFill: ttfFmt,
    timeToFillNumber,
  } = (() => {
    const entity = postingData?.posting?.[0];
    if (!entity) {
      return {
        postings: '-',
        timeToFill: '-',
      };
    }

    const lastPosting = entity.category?.[entity.category.length - 1];
    const activePostings = lastPosting?.metrics?.active;
    const timeToFill = lastPosting?.metrics?.time_to_fill;

    return {
      postings: activePostings ? formatInt(activePostings) : '-',
      postingsNumber: activePostings,
      timeToFill: timeToFill ? formatInt(timeToFill) : '-',
      timeToFillNumber: timeToFill,
    };
  })();

  const salaryData = (() => {
    if (!compositionTooltipData) return [];
    if (isV3Tooltip && 'composition_v3' in compositionTooltipData) {
      return (
        compositionTooltipData.composition_v3?.entities?.[0]?.metrics?.salary ??
        []
      );
    }
    if (!('composition_v2_tooltip' in compositionTooltipData)) {
      return [];
    }
    return (
      compositionTooltipData.composition_v2_tooltip?.entities?.[0]?.scaled
        ?.salary ?? []
    );
  })();

  const yearStartWage = (() => {
    const first = salaryData[0];
    if (first === undefined) return 0;
    if (isV3Tooltip && 'val' in first) {
      return first.val;
    }
    if ('value' in first && first.value !== undefined && first.value !== null) {
      return first.value;
    }
    return 0;
  })();

  const yearEndWage = (() => {
    const last = salaryData[salaryData.length - 1];
    if (last === undefined) return 0;
    if (isV3Tooltip && 'val' in last) {
      return last.val;
    }
    if ('value' in last && last.value !== undefined && last.value !== null) {
      return last.value;
    }
    return 0;
  })();
  const wageGrowth =
    yearStartWage !== 0 ? (yearEndWage - yearStartWage) / yearStartWage : 0;
  const marketTightness = calculateMarketTightnessMeterValue({
    wageGrowth,
    demandSupply: Number(postingsNumber) / Number(headcountNumber),
    timeToFill: Number(timeToFillNumber),
  });

  return {
    header:
      (tooltipFeature.label || tooltipFeature.location) ?? 'Unknown Geography',
    headcount: headcountFmt,
    postings: postingFmt,
    timeToFill: ttfFmt,
    salaryDist: getPayPercentiles(distributionData, currencyFormatter),
    marketTightness: (marketTightness * 100).toString(),
    isLoading: compositionFetching || postingFetching || compensationFetching,
    showSalaryData: !skipCompensation,
  };
}

export const useFetchTooltipDataLegacy = (
  params: UseFetchTooltipDataParams
): TooltipData | null => useFetchTooltipDataInner('legacy', params);

export const useFetchTooltipDataV3 = (
  params: UseFetchTooltipDataParams
): TooltipData | null => useFetchTooltipDataInner('v3', params);

export const useFetchTooltipData = (
  params: UseFetchTooltipDataParams
): TooltipData | null => {
  const isV3 = useUnleashFlag(FeatureFlag.CompositionV3);
  return useFetchTooltipDataInner(isV3 ? 'v3' : 'legacy', params);
};

type GeographyFilterKeys = 'region' | 'country' | 'msa' | 'state' | 'city';

export const replaceGeographyFilters = <T extends Record<string, unknown>>(
  filters: T | undefined,
  geoFilter: Partial<Record<GeographyFilterKeys, unknown>> | null
): T => {
  if (!geoFilter) return (filters ?? {}) as T;

  const {
    region: _region,
    country: _country,
    msa: _msa,
    state: _state,
    city: _city,
    ...rest
  } = (filters ?? {}) as T;

  return {
    ...rest,
    ...geoFilter,
  } as T;
};

const getTooltipGeoFilter = (
  tooltipFeature: GeoJsonProperties | null,
  hasGranularSelection: boolean
):
  | (Pick<
      CompositionV2FullSnapshotReq['filters'],
      'country' | 'msa' | 'city' | 'state'
    > &
      Pick<PostingFilters, 'country' | 'msa' | 'city' | 'state'>)
  | null => {
  if (
    !tooltipFeature ||
    !('id' in tooltipFeature) ||
    !('type' in tooltipFeature)
  ) {
    return null;
  }
  const id = Number(tooltipFeature['id']);
  if (Number.isNaN(id)) return null;

  if (tooltipFeature['type'] === 1) {
    if (hasGranularSelection) return null;
    return { country: [id] };
  }
  if (tooltipFeature['type'] === 2) return { msa: [id] };
  if (tooltipFeature['type'] === 3) return { city: [id] };
  if (tooltipFeature['type'] === 4) return { state: [id] };

  return null;
};

const hasGranularGeoSelection = (
  filters: CompositionV2Filters | PostingFilters | undefined | null
): boolean => {
  if (!filters) return false;

  const hasValues = (
    value: Array<number | string | null | undefined> | null | undefined
  ) => Array.isArray(value) && value.length > 0;

  return (
    hasValues(filters.msa) ||
    hasValues(filters.city) ||
    hasValues(filters.state)
  );
};

const buildTooltipEntities = (
  tooltipFeature: GeoJsonProperties | null,
  fallbackEntities: CompositionV2Entity[]
): CompositionV2Entity[] => {
  if (!tooltipFeature) return fallbackEntities;
  const entId = Number(tooltipFeature['id']);
  if (Number.isNaN(entId)) return fallbackEntities;

  const type = tooltipFeature['type'];
  if (type === 1)
    return [{ EntID: entId, EntType: PositionsEntityKind.Country }];
  if (type === 2) return [{ EntID: entId, EntType: PositionsEntityKind.Msa }];
  if (type === 3) return [{ EntID: entId, EntType: PositionsEntityKind.City }];
  if (type === 4) return [{ EntID: entId, EntType: PositionsEntityKind.State }];

  return fallbackEntities;
};

const getPayPercentiles = (
  distributionData: DistributionData[],
  currencyFormatter: (value: number) => string
) => {
  if (!distributionData || distributionData.length === 0) return [] as string[];
  const first = distributionData[0];
  if (!first || !first.distribution || first.distribution.length === 0) {
    return [] as string[];
  }

  const salaries = first.distribution.map((d) => d.value);
  // Convert CDF ratios (0-1) to percentage scale (0-100) for easier comparison
  const cdf = first.distribution.map((d) => d.cdf * 100);

  const getPercentiles = ({
    salaries = [],
    cdf = [],
    percentiles,
  }: {
    salaries: number[];
    cdf: number[];
    percentiles: number[];
  }) => {
    return percentiles.map((percentile) => {
      let nearest = cdf[0];
      // @ts-expect-error noUncheckedIndexedAccess
      let smallestDiff = Math.abs(cdf[0] - percentile);
      for (let i = 1; i < cdf.length; i++) {
        // @ts-expect-error noUncheckedIndexedAccess
        const difference = Math.abs(cdf[i] - percentile);
        if (difference < smallestDiff) {
          smallestDiff = difference;
          nearest = cdf[i];
        }
      }
      // biome-ignore lint/complexity/useIndexOf: migrated from develop
      const cdfIndex = cdf.findIndex((item) => item === nearest);
      return cdfIndex === -1 ? 0 : salaries[cdfIndex] || 0;
    });
  };

  return getPercentiles({
    salaries,
    cdf,
    percentiles: [10, 50, 90],
  }).map((val) => currencyFormatter(val));
};
